import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import Button from '../../components/BaseComponents/Button';
import { changeModalVisibility } from '../../store/adminsSlice';
import AddAdminModal from '../../components/Admins/AdminModal';
import AdminsTable from '../../components/Admins/AdminsTable';
import { useNavigate } from 'react-router';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';
import { AdminMember, getAdminsList, getAdminById, updateAdminStatus } from '../../api/admin';
import { toast } from 'react-toastify';
import './AdministratorsPage.scss';

type AdminsProps = {};

const Admins: React.FC<AdminsProps> = () => {
  const [admins, setAdmins] = useState<AdminMember[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminMember | null>(null);
  const LIMIT = 10;

  const {
    admin: { role },
  } = useSelector((state: RootState) => state.admin);
  const navigate = useNavigate();

  const fetchAdmins = useCallback(async (isRefresh: boolean = false) => {
    setIsLoading(true);

    try {
      const currentOffset = isRefresh ? 0 : offset;
      const data = await getAdminsList(LIMIT, currentOffset);
      const newAdmins = data.admins || [];

      setAdmins(prev => isRefresh ? newAdmins : [...prev, ...newAdmins]);

      const count = data['total count'] !== undefined ? data['total count'] :
        (data as any).total_count !== undefined ? (data as any).total_count :
          newAdmins.length;

      setTotalCount(count);
      setHasMore(isRefresh ? newAdmins.length < count : (admins.length + newAdmins.length) < count);
      setOffset(currentOffset + LIMIT);

    } catch (error: any) {
      console.error('Error fetching admins:', error);
      toast.error(error.message || 'Failed to fetch administrators');
    } finally {
      setIsLoading(false);
    }
  }, [offset, admins.length]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (isBottom && !isLoading && hasMore) {
      fetchAdmins();
    }
  };

  useEffect(() => {
    if (role === UserRoleEnum.Doctor || role === UserRoleEnum.Nutritionist) {
      navigate('/members');
    } else if (role !== UserRoleEnum.SuperAdmin && role !== null) {
      navigate('/members');
    }

    if (role === UserRoleEnum.SuperAdmin && admins.length === 0 && isLoading) {
      fetchAdmins(true);
    }
  }, [role, navigate, admins.length, isLoading, fetchAdmins]);

  const dispatch = useDispatch();
  const { isAddAdminModalOpened } = useSelector(
    (state: RootState) => state.admins
  );

  const onAddAdminBtnClick = () => {
    setSelectedAdmin(null);
    dispatch(changeModalVisibility(true));
  };
  const onModalClose = () => {
    dispatch(changeModalVisibility(false));
    setSelectedAdmin(null);
  };

  const handleEdit = async (id: string | number) => {
    try {
      const adminDetails = await getAdminById(id);
      setSelectedAdmin(adminDetails);
      dispatch(changeModalVisibility(true));
    } catch (error: any) {
      console.error('Error fetching admin details:', error);
      toast.error(error.message || 'Failed to fetch admin details');
    }
  };

  const handleDisable = async (id: string | number) => {
    const adminToToggle = admins.find(a => a.id === id);
    if (!adminToToggle) return;

    try {
      await updateAdminStatus(id, !adminToToggle.is_active);
      toast.success(`Admin ${adminToToggle.is_active ? 'disabled' : 'activated'} successfully`);
      fetchAdmins(true);
    } catch (error: any) {
      console.error('Error updating admin status:', error);
      toast.error(error.message || 'Failed to update admin status');
    }
  };

  return (
    <div className='page-container'>
      <div className='search-row-container sideBarPad'>
        <div className='page-title-section'>
          <h1 className='page-title'>Admin Section ({totalCount})</h1>
        </div>
        <Button
          isSecondary={true}
          text='Add administrator'
          onClick={onAddAdminBtnClick}
        />
      </div>

      <AdminsTable
        admins={admins}
        isLoading={isLoading}
        onScroll={onScroll}
        onEdit={handleEdit}
        onDisable={handleDisable}
      />

      <AddAdminModal
        isOpen={isAddAdminModalOpened}
        onClose={onModalClose}
        onSuccess={() => fetchAdmins(true)}
        adminToEdit={selectedAdmin}
      />
    </div>
  );
};
export default Admins;
